import { useEffect, useRef } from 'react';
import { default as useEventSink } from './useEventSink';
import metaDataTransforms from '@sstdev/lib_metadata-transforms';
import { constants } from 'lib_ui-services';

const { Blockly } = metaDataTransforms;

const _p = { useEventSink };

export default function useWorkspaceActionSubscriptions({
    namespace,
    relation,
    metadataRecord,
    blocklyConfigurationInWorkspace,
    toolboxReady,
    workspace,
    useCaseDetail
}) {
    const [subscribe, publish, request] = _p.useEventSink();
    // Keep the last committed source separate from the live workspace. Updating
    // metadataRecord would reload Blockly and discard edits made during a save.
    const savedRecord = useRef(metadataRecord);
    useEffect(() => {
        savedRecord.current = metadataRecord;
    }, [metadataRecord, namespace, relation, workspace]);

    useEffect(() => {
        const unsubscribes = [
            subscribe({ verb: 'save', namespace, relation }, async () => {
                try {
                    const original = savedRecord.current;
                    const replacement = { ...original, blockly: blocklyConfigurationInWorkspace };
                    const result = await request(
                        {
                            'metadata:useCaseDetail': useCaseDetail,
                            [`${namespace}:${relation}`]: replacement
                        },
                        { verb: 'create', namespace: 'metaui', relation: 'dataDictionary' }
                    );
                    if (result.errors && result.errors.length > 0) {
                        publish(
                            {
                                message: 'Save unsuccessful due to validation errors.',
                                addToList: false,
                                isError: true, //todo: gl2025 remove
                                variant: 'error',
                                noCancelOption: true
                            },
                            { verb: 'confirm', namespace: 'application', relation: 'user' }
                        );
                        return;
                    }
                    const updated = await request(
                        {
                            oldRecord: original,
                            newRecord: replacement
                        },
                        { verb: 'update', namespace, relation }
                    );
                    // A different record may have opened while the request was pending.
                    if (savedRecord.current === original && updated?.newRecord) savedRecord.current = updated.newRecord;
                    publish(
                        {
                            message: 'Saved successfully',
                            timeout: 1000,
                            addToList: false,
                            isError: false, //todo: gl2025 remove
                            variant: 'success',
                            type: constants.notificationTypes.TOAST
                        },
                        { verb: 'pop', namespace: 'application', relation: 'notification' }
                    );
                } catch (error) {
                    publish(
                        {
                            message: error.message,
                            variant: 'error',
                            type: constants.notificationTypes.TOAST
                        },
                        { verb: 'pop', namespace: 'application', relation: 'notification' }
                    );
                    throw error;
                }
            }),
            // "close" verb is hijacked to clear the workspace (but keeps the record active!)
            subscribe({ verb: 'close', namespace, relation }, () => {
                if (toolboxReady && metadataRecord) {
                    workspace?.clear?.();
                }
            }),
            //"refresh" verb for reloading the original (stored) record
            subscribe({ verb: 'refresh', namespace, relation }, () => {
                if (toolboxReady && metadataRecord) {
                    workspace?.clear?.();
                    workspace?.clearUndo?.();
                    workspace?.trashcan?.emptyContents?.();
                    Blockly?.serialization?.workspaces?.load?.(savedRecord.current.blockly, workspace);
                }
            }),
            subscribe({ verb: 'view', namespace: 'metaui', relation: 'validations' }, () => {
                //creating the data dictionary is what runs the validations
                //see /lib_metadata-transforms/packages/lib_metadata-transforms/src/dataDictionaryService/relationProperties.js
                //any validation errors are dispatch as error notifications. see line 95 "metaDataTransforms.setValidationReporter" above.
                publish(
                    {
                        'metadata:useCaseDetail': useCaseDetail,
                        [`${namespace}:${relation}`]: { ...metadataRecord, blockly: blocklyConfigurationInWorkspace }
                    },
                    { verb: 'create', namespace: 'metaui', relation: 'dataDictionary' }
                );
            }),
            subscribe({ verb: 'view', namespace: 'metaui', relation: 'dataDictionary', status: 'success' }, () => {
                // by blockly "view" should open a simpleDialog with a data Dictionary viewer
                // that viewer will send the success to trigger this call.
                // the result of this call should then populate that viewer.
                publish(
                    {
                        'metadata:useCaseDetail': useCaseDetail,
                        [`${namespace}:${relation}`]: { ...metadataRecord, blockly: blocklyConfigurationInWorkspace }
                    },
                    { verb: 'create', namespace: 'metaui', relation: 'dataDictionary' }
                );
            })
        ];

        return () => {
            unsubscribes.forEach(u => u());
        };
    }, [
        subscribe,
        request,
        publish,
        namespace,
        relation,
        metadataRecord,
        blocklyConfigurationInWorkspace,
        toolboxReady,
        workspace,
        useCaseDetail
    ]);
}

export const _private = _p;
